import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    UserFragment,
    Users_Insert_Input,
    UpdateUserMutationVariables,
    User_Roles_Enum,
    User_Type_Enum,
    User_Status_Enum
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    AgencyOperationsAdminTalentCreateFragment, 
    AgencyOperationsAdminTalentCreateSchema, 
    AgencyOperationsAdminTalentUpdateFragment, 
    AgencyOperationsAdminTalentUpdateSchema 
} from "src/components/agency/operations/administrator/talent/AgencyOperationsAdminTalentSchemas"
import { extractCurrentItemData } from "src/utils/forms"
import { uuidToVibrantHexColor } from "src/utils/calendar"

type FormPayloadType = AgencyOperationsAdminTalentCreateFragment

let initialState: FormPayloadType = {
    email: "",
    genres: "",
    color: uuidToVibrantHexColor(crypto.randomUUID()),
    timezone: 'America/New_York'
};

export const CreateAgencyOperationsAdminTalentDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().users;
    const { loading, errors } = useAppState().users;
    const { tenant_id } = useAppState().auth;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Users_Insert_Input) => {
        return createItem({
            tenant_id,
            role: User_Roles_Enum.AgencyTalentAdministrator,
            type: User_Type_Enum.Talent,
            ...payload
        })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, UserFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminTalentCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateAgencyOperationsAdminTalentAction = () => {
    return (
        <PageDrawerAction<UserFragment>
            title='Add Talent'
            action={CreateAgencyOperationsAdminTalentDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: AgencyOperationsAdminTalentUpdateFragment = {
    status: User_Status_Enum.Active,
    email: "",
    first_name: "",
    last_name: "",
    alias: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    bday: "",
    phone: "",
    website: "",
    instagram: "",
    facebook: "",
    x: "",
    tiktok: "",
    genres: "",
    color: "",
    timezone: 'America/New_York'
};

export const UpdateAgencyOperationsAdminTalentDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().users;
    const { currentItem, loading, errors } = useAppState().users;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: AgencyOperationsAdminTalentUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<AgencyOperationsAdminTalentUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateUserMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<AgencyOperationsAdminTalentUpdateFragment, UserFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminTalentUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateAgencyOperationsAdminTalentAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().users;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.users.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<UserFragment>
            title='Edit Talent'
            action={UpdateAgencyOperationsAdminTalentDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditAgencyOperationsAdminTalentAction = () => {
    return (
        <PageDrawerAction<UserFragment>
            title='Edit Talent'
            action={UpdateAgencyOperationsAdminTalentDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}